import * as React from 'react';
import Styled from 'styled-components';

import { FONT_MAP } from '../../lib/StyledComponents';
import { DatapointLabel } from '../Typography';

const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;
const MAX_X = 100;
const MAX_Y = 100;
const FULL_RADIUS = 50;
const CENTER_X = 50;
const CENTER_Y = 50;

const RadialProgress = Styled.div`
  position: relative;
`;

const RadialGraphic = Styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column;

  svg {
    path {
      stroke: ${({ theme: { brand } }) => brand.colorSecondary};

      &:first-child {
        stroke: ${({ theme: { progress } }) => progress.colorCircularBackground};
      }
    }
  }
`;

const RadialLabel = Styled.div`
  font-size: 1.4rem;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const LabelPercent = Styled.span`
  display: flex;
  font-family: ${({ theme: { typography } }) => FONT_MAP[typography.displayTypeface]};
  color: ${({ theme: { text } }) => text.colorText};
  font-size: ${({ theme: { progress } }) => progress.circularSize};
  font-weight: ${({ theme: { progress } }) => progress.circularWeight};
  line-height: 1em;
`;

const LabelUnit = Styled.span`
  font-weight: 300;
  display: flex;
  line-height: 2.8rem;
  font-size: 2.4rem;
`;

interface ILabelLegendProps {
  textOffset?: string;
}

const LabelLegend = Styled(DatapointLabel)<ILabelLegendProps>`
  font-weight: ${({ theme: { progress } }) => progress.circularWeight};
  padding-bottom: ${props => props.textOffset};
`;

LabelLegend.defaultProps = {
  textOffset: '3px'
};

export interface ICircularProgressProps {
  percentage: number;
  label?: string;
  strokeWidth?: number;
  textOffset?: string;
  animate?: boolean;
  animationDelay?: string;
}

export interface ICircularProgressState {
  isInitialized: boolean;
}

type PathProperties = Pick<React.CSSProperties, 'strokeDasharray' | 'strokeDashoffset' | 'transition' | 'transitionDelay'>;

export class CircularProgress extends React.Component<ICircularProgressProps, ICircularProgressState> {
  public static defaultProps: Partial<ICircularProgressProps> = {
    strokeWidth: 3,
    animationDelay: '1s'
  };

  public state: ICircularProgressState = {
    isInitialized: false
  };

  private requestAnimationFrame: number | undefined;

  public componentDidMount (): void {
    if (this.props.animate) {
      this.requestAnimationFrame = window.requestAnimationFrame(() => {
        this.setState({
          isInitialized: true
        });
      });
    }
  }

  public componentWillUnmount (): void {
    if (this.requestAnimationFrame) {
      window.cancelAnimationFrame(this.requestAnimationFrame);
    }
  }

  public render (): JSX.Element {
    const {
      strokeWidth,
      ...rest
    } = this.props;
    const pathDescription = this.getPathDescription();

    return (
      <RadialProgress {...rest}>
        <RadialGraphic>
          <svg
            viewBox={`0 0 ${MAX_X} ${MAX_Y}`}
            aria-hidden={true}
          >
            {/* background path */}
            <path
              d={pathDescription}
              strokeWidth={strokeWidth}
              fillOpacity={0}
            />
            {/* percent path */}
            <path
              d={pathDescription}
              strokeWidth={strokeWidth}
              fillOpacity={0}
              style={this.getPathStyles()}
              strokeLinecap='round'
            />
          </svg>
        </RadialGraphic>
        {this.props.children}
      </RadialProgress>
    );
  }

  private getPathDescription (): string {
    const radius = this.getPathRadius();
    const rotation = 0;

    // Move to center of canvas
    // Relative move to top canvas
    // Relative arc to bottom of canvas
    // Relative arc to top of canvas
    return `
      M ${CENTER_X},${CENTER_Y}
      m 0,-${radius}
      a ${radius},${radius} ${rotation} 1 1 0,${radius * 2}
      a ${radius},${radius} ${rotation} 1 1 0,-${radius * 2}
    `;
  }

  private getPathStyles (): PathProperties {
    const diameter = Math.PI * 2 * this.getPathRadius();
    const truncatedPercentage = Math.min(
      Math.max(this.percentage, MIN_PERCENTAGE),
      MAX_PERCENTAGE
    );
    const dashoffset = ((100 - truncatedPercentage) / 100) * diameter;

    return {
      strokeDasharray: `${diameter}px ${diameter}px`,
      strokeDashoffset: `${dashoffset}px`,
      transition: this.props.animate ? 'all 600ms cubic-bezier(0.455, 0.03, 0.515, 0.955)' : 'initial',
      transitionDelay: this.props.animationDelay ? this.props.animationDelay : 'initial'
    };
  }

  private getPathRadius (): number {
    // the radius of the path is defined to be in the middle, so in order for the path to
    // fit perfectly inside the 100x100 viewBox, need to subtract half the strokeWidth
    // tslint:disable-next-line:no-non-null-assertion
    return FULL_RADIUS - this.props.strokeWidth! / 2;
  }

  private get percentage (): number {
    if (this.props.animate && !this.state.isInitialized) {
      return 0;
    }

    return this.props.percentage;
  }
}

export const CircularProgressWithLabel: React.FunctionComponent<ICircularProgressProps> = props => (
  <CircularProgress {...props}>
    <RadialLabel>
      <LabelPercent>
        <span>{props.percentage}</span>
        <LabelUnit>%</LabelUnit>
      </LabelPercent>
      <LabelLegend textOffset={props.textOffset}>{props.label}</LabelLegend>
    </RadialLabel>
  </CircularProgress>
);
